import { useState } from "react";
import { ChevronDown } from "lucide-react";
import RichBlock from "@/components/vetting/RichBlock";
import { CopyButton, ForYou, RunsText } from "@/components/appointments/MessageCard";

const FEATURED = /^(WATCH PROCESS TRAINING|PROCESS OVERVIEW)/i;
const INLINE = /^(START HERE|THE PROCESS)$/i;
const MG1_FORM = /^MG1 REQUEST FORM/i; // approved: always open on entering Start Here

function Expandable({ sec, testid, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-slate-200 bg-white" data-testid={testid} data-open={open ? "true" : "false"}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={`${testid}-panel`} data-testid={`${testid}-toggle`}
              className="w-full min-h-[48px] flex items-center gap-3 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A961] rounded-xl">
        <span className="flex-1 text-sm font-semibold text-[#0A1F44]">{sec.label}</span>
        <ChevronDown aria-hidden="true" className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div id={`${testid}-panel`} className="px-4 pb-4 space-y-3">
          {sec.copy_text && <div className="flex justify-end"><CopyButton text={sec.copy_text} label="Copy form" testid={`${testid}-copy`} /></div>}
          {sec.copy_text ? (
            <>
              <p className="text-sm text-slate-600">{sec.body.map((r) => r.t).join("").split(sec.copy_text.slice(0, 12))[0].trim()}</p>
              <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={`${testid}-form`}>{sec.copy_text}</div>
            </>
          ) : <RichBlock runs={sec.body} testid={`${testid}-body`} />}
          <ForYou runs={sec.note} testid={`${testid}-note`} />
        </div>
      )}
    </section>
  );
}

export default function StartHere({ stage }) {
  const featured = stage.sections.filter((s) => FEATURED.test(s.label));
  const inline = stage.sections.filter((s) => INLINE.test(s.label));
  const rest = stage.sections.filter((s) => !FEATURED.test(s.label) && !INLINE.test(s.label));
  return (
    <div className="space-y-5" data-testid="appt-start">
      <div className="space-y-4">
        {featured.map((s) => (
          <div key={s.label} data-testid={`appt-start-featured-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold mb-1.5">{s.label}</div>
            <RichBlock runs={s.body} testid={`appt-start-feat-${s.label.length}`} />
          </div>
        ))}
      </div>
      {inline.map((s) => (
        <section key={s.label} className="rounded-2xl border-l-4 border-[#C9A961] bg-[#F7F5EF] p-4" data-testid={`appt-start-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold mb-1">{s.label}</div>
          <RunsText runs={s.body} />
          {s.note?.some((r) => r.u) ? <div className="mt-3"><RichBlock runs={s.note} testid={`appt-start-res-${s.label.length}`} /></div> : <ForYou runs={s.note} />}
        </section>
      ))}
      <div className="space-y-2">
        {rest.map((s, i) => <Expandable key={i} sec={s} defaultOpen={MG1_FORM.test(s.label)} testid={`appt-start-sec-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />)}
      </div>
    </div>
  );
}
